import moment from 'moment'
import { RoomScheduledRule, ScheduledSessionOfUser } from '../../AppContext'
import { getCurrentIanaTimezone, stripSemverVersion } from '../../utils'
import { env } from '../../services/EnvironmentVariables'
import { datetime, RRule, rrulestr, Weekday } from 'rrule'
import { DateTime } from 'luxon'

// number of minutes since 0 am, used for the h:mm time selection dial
export type TimeSelection = number

export type CreateSessionFormData = {
    sessionName: string
    username: string
    startDate: Date
    startTimeSelection: TimeSelection
    endTimeSelection: TimeSelection
    timeZoneCode: string
    collaboratorUsernames: string[]
    password: string
    calendarIntegrationSelection: 'google' | ''
    rrule: RoomScheduledRule | null
    cloudRecordingEnabled: boolean
}

export class RepeatRule {
    _repeatMenuPreset: 'repeat_daily' | 'repeat_weekly' | 'repeat_monthly' | 'repeat_custom' | null = null
    _monthlyRepeatType: 'by_date' | 'by_weekday' = 'by_date'

    // valid when `_monthlyRepeatType === 'by_weekday'`
    _monthlyRepeatByweekday: number = 0 // Sunday, Monday, etc.
    _monthlyRepeatByweekdayNth: number = 1 // corresponds to "first", "second", etc in "first Monday of the month", etc. `-1` indicates 'last'.

    freq: 'daily' | 'weekly' | 'monthly' | null = null
    interval: number = 1
    byhour: number | null = null
    byminute: number | null = null
    byweekday: number[] = []
    bymonthday: number | null = null
    dtstart: string /*ISO Date string*/ | null = null
    count: number | null = null
    until: Date | null = null
    tzid: string = getCurrentIanaTimezone()
}

export function toRruleDate(dateStr: string, tzCode: string) {
    const d = DateTime.fromISO(dateStr, { zone: tzCode })

    return datetime(d.year, d.month, d.day, d.hour, d.minute, 0)
}

export function fromRruleDate(date: Date) {
    const year = date.getUTCFullYear()
    const month = date.getUTCMonth()
    const day = date.getUTCDate()
    const hours = date.getUTCHours()
    const minutes = date.getUTCMinutes()

    return new Date(year, month, day, hours, minutes)
}

export function toRRule(_r: RepeatRule) {
    const r = structuredClone(_r)

    let freq = undefined
    if (r.freq === 'daily') {
        freq = RRule.DAILY
    } else if (r.freq === 'weekly') {
        freq = RRule.WEEKLY
    } else if (r.freq === 'monthly') {
        freq = RRule.MONTHLY
    }

    if (!freq) {
        return null
    }

    let byweekday: Weekday[] | Weekday | null = null
    if (r.freq === 'weekly') {
        if ((r.byweekday || []).length === 0) {
            r.byweekday = [0]
        }

        if (!r.byweekday || r.byweekday.length > 0) {
            byweekday = r.byweekday.map((a) => {
                switch (a) {
                    case 0:
                        return RRule.SU
                    case 1:
                        return RRule.MO
                    case 2:
                        return RRule.TU
                    case 3:
                        return RRule.WE
                    case 4:
                        return RRule.TH
                    case 5:
                        return RRule.FR
                    case 6:
                        return RRule.SA
                }
                return RRule.SU
            })
        }
    }

    let bymonthday = null
    if (r.freq === 'monthly') {
        if (r._monthlyRepeatType === 'by_weekday') {
            const nth = r._monthlyRepeatByweekdayNth || 1
            const byweekdayCandidate = (() => {
                switch (r._monthlyRepeatByweekday) {
                    case 0:
                        return RRule.SU.nth(nth)
                    case 1:
                        return RRule.MO.nth(nth)
                    case 2:
                        return RRule.TU.nth(nth)
                    case 3:
                        return RRule.WE.nth(nth)
                    case 4:
                        return RRule.TH.nth(nth)
                    case 5:
                        return RRule.FR.nth(nth)
                    case 6:
                        return RRule.SA.nth(nth)
                }
            })()

            if (byweekdayCandidate) {
                byweekday = byweekdayCandidate
            }
        } else if (r._monthlyRepeatType === 'by_date') {
            bymonthday = r.bymonthday
        }
    }

    const options = {
        freq: freq,
        interval: r.interval,
        byhour: r.byhour,
        byminute: r.byminute,
        byweekday: byweekday,
        bymonthday: bymonthday,
        dtstart: r.dtstart ? toRruleDate(r.dtstart, r.tzid) : null,
        count: r.count,
        until: r.until,
        tzid: r.tzid,
    }

    const rrule = new RRule(options)
    return { rrule, origOptions: r }
}

// Function to generate time options in 15-minute increments
export function generateTimeSelectionOptions(): { label: string; value: string }[] {
    const timeOptions = []
    for (let h = 0; h < 24; h++) {
        for (let m = 0; m < 60; m += 15) {
            const t = h * 60 + m
            timeOptions.push({
                label: formatTimeSelection(t),
                value: t.toString(),
            })
        }
    }
    return timeOptions
}

export function normalizeTimeSelection(t: TimeSelection): TimeSelection {
    if (t >= 60 * 24) {
        return t - 60 * 24
    }
    return t
}

export function getDefaultStartTimeSelection(): TimeSelection {
    const nextHour = moment().startOf('hour').hour() + 1
    return normalizeTimeSelection(nextHour * 60)
}

export function getDefaultEndTimeSelection(): TimeSelection {
    const nextHour = moment().startOf('hour').hour() + 2
    return normalizeTimeSelection(nextHour * 60)
}

export function addTimeSelection(a: TimeSelection, b: TimeSelection): TimeSelection {
    const next = a + b
    return normalizeTimeSelection(next)
}

export function parseTimeSelection(t: TimeSelection) {
    const hour = Math.floor(t / 60)
    const min = Math.floor(t - hour * 60)
    const mRounded = Math.round(min / 15) * 15

    return { hour, min: mRounded }
}

export function formatTimeSelection(t: TimeSelection): string {
    let { hour, min } = parseTimeSelection(t)

    let ampm = 'AM'

    if (hour > 12) {
        hour -= 12
        ampm = 'PM'
    } else if (hour === 0) {
        hour = 12
    } else if (hour === 12) {
        ampm = 'PM'
    }

    const hourStr = hour.toString()
    const minStr = min.toString().padStart(2, '0')
    return `${hourStr}:${minStr} ${ampm}`
}

export function generateDefaultSessionName(username: string) {
    if (username) {
        return `${username}'s session`
    } else {
        return `Room`
    }
}

export function mkInitialCollaboratorUsernames(editingSession: ScheduledSessionOfUser | null | undefined) {
    if (!editingSession) {
        return []
    }

    return editingSession.users.filter((a) => !a.isHost).map((a) => a.username)
}

export function mkInitialStartDate(editingSession: ScheduledSessionOfUser | null | undefined) {
    if (!editingSession?.scheduledStartTime) {
        return new Date()
    }

    const t = editingSession.scheduledTimeZone
        ? DateTime.fromISO(editingSession.scheduledStartTime).setZone(editingSession.scheduledTimeZone)
        : DateTime.fromISO(editingSession.scheduledStartTime)

    if (!t.isValid) {
        return new Date()
    }

    const date = new Date(`${t.year}-${t.month}-${t.day}`)
    return date
}

export function mkInitialStartTimeSelection(editingSession: ScheduledSessionOfUser | null | undefined): TimeSelection {
    if (!editingSession?.scheduledStartTime) {
        return getDefaultStartTimeSelection()
    }

    const t = editingSession.scheduledTimeZone
        ? DateTime.fromISO(editingSession.scheduledStartTime).setZone(editingSession.scheduledTimeZone)
        : DateTime.fromISO(editingSession.scheduledStartTime)

    const h = t.hour
    const mRounded = Math.round(t.minute / 15) * 15

    return h * 60 + mRounded
}

export function mkInitialEndTimeSelection(editingSession: ScheduledSessionOfUser | null | undefined): TimeSelection {
    if (!editingSession?.scheduledEndTime) {
        return getDefaultEndTimeSelection()
    }

    const t = editingSession.scheduledTimeZone
        ? DateTime.fromISO(editingSession.scheduledEndTime).setZone(editingSession.scheduledTimeZone)
        : DateTime.fromISO(editingSession.scheduledEndTime)

    const h = t.hour
    const mRounded = Math.round(t.minute / 15) * 15

    return h * 60 + mRounded
}

export const toFullStartAndEndTimes = ({
    startDate,
    startTimeSelection,
    endTimeSelection,
    timeZoneCode,
}: {
    startDate: Date
    startTimeSelection: TimeSelection
    endTimeSelection: TimeSelection
    timeZoneCode: string
}) => {
    /*const scheduledStartTime = (() => {
        const ymd = moment(startDate).format('YYYY-MM-DD')
        return moment(`${ymd} ${startTimeSelection}`, 'YYYY-MM-DD h:mm A').toISOString()
    })()

    const scheduledEndTime = (() => {
        const ymd = moment(startDate).format('YYYY-MM-DD')
        let t = moment(`${ymd} ${endTimeSelection}`, 'YYYY-MM-DD h:mm A')
        if (isEndLtStartTime(startTimeSelection, endTimeSelection)) {
            t = t.add(1, 'day')
        }
        return t.toISOString()
    })()*/

    const scheduledStartTime = (() => {
        const zone = timeZoneCode

        const y = startDate.getFullYear().toString()
        const m = `${(startDate.getMonth() + 1).toString().padStart(2, '0')}`
        const d = `${startDate.getDate().toString().padStart(2, '0')}`

        const t = DateTime.fromFormat(`${y}-${m}-${d}`, 'yyyy-MM-dd', { zone })
        return t.plus({ minutes: startTimeSelection }).toISO()
    })()

    const scheduledEndTime = (() => {
        const zone = timeZoneCode

        const y = startDate.getFullYear().toString()
        const m = `${(startDate.getMonth() + 1).toString().padStart(2, '0')}`
        const d = `${startDate.getDate().toString().padStart(2, '0')}`

        let t = DateTime.fromFormat(`${y}-${m}-${d}`, 'yyyy-MM-dd', { zone })
        t = t.plus({ minutes: endTimeSelection })

        if (endTimeSelection < startTimeSelection) {
            t = t.plus({ days: 1 })
        }

        return t.toISO()
    })()

    return { scheduledStartTime, scheduledEndTime }
}

export const createScheduledSession = async ({
    formData,
    editingSession,
    createRoom,
}: {
    formData: CreateSessionFormData
    editingSession?: ScheduledSessionOfUser
    createRoom: any
}) => {
    const {
        startDate,
        startTimeSelection,
        endTimeSelection,
        sessionName,
        username,
        collaboratorUsernames,
        timeZoneCode,
        password,
        calendarIntegrationSelection,
        rrule,
        cloudRecordingEnabled,
    } = formData

    const { scheduledStartTime, scheduledEndTime } = toFullStartAndEndTimes({
        startDate,
        startTimeSelection,
        endTimeSelection,
        timeZoneCode,
    })

    const body: any = {
        name: sessionName.trim() || generateDefaultSessionName(username),
        collaborators: collaboratorUsernames,
        allowJoiningFromCode: true,

        scheduledStartTime,
        scheduledEndTime,
        scheduledTimeZone: timeZoneCode,
        scheduledRrule: rrule,
        calendarIntegrationSelection,
        cloudRecordingEnabled,

        password: password,

        createdFromAppVersion: stripSemverVersion(env.APP_VERSION),
    }

    // setting the roomUrn --> editing an existing room.
    if (editingSession) {
        body.roomUrn = editingSession.roomUrn
    }

    try {
        const result = await createRoom(body)
        return [result, null]
    } catch (e) {
        console.error(e)
        const message = `There was an error while processing the request.`
        // RootSnackbar.show({ severity: 'error', message })
        return [null, { severity: 'error', message }]
    }
}

export const getOrdinalSuffix = (day: number) => {
    if (day > 3 && day < 21) return 'th' // Covers 11th, 12th, 13th, etc.
    switch (day % 10) {
        case 1:
            return 'st'
        case 2:
            return 'nd'
        case 3:
            return 'rd'
        default:
            return 'th'
    }
}

export function getSessionsInWindow(
    scheduledSessions: ScheduledSessionOfUser[],
    windowStart: Date,
    windowEnd: Date,
    currTimeZone: string
): ScheduledSessionOfUser[] {
    const result: ScheduledSessionOfUser[] = []

    for (const session of scheduledSessions) {
        if (session.scheduledRrule) {
            try {
                const parsedRule = rrulestr(session.scheduledRrule.icalStr)

                const rruleWindowStart = toRruleDate(windowStart.toISOString(), currTimeZone)
                const rruleWindowEnd = toRruleDate(windowEnd.toISOString(), currTimeZone)

                const occurrences = parsedRule.between(rruleWindowStart, rruleWindowEnd, true).map(fromRruleDate)

                for (const occurrence of occurrences) {
                    const patch = (session.scheduledRrule.patches || {})[
                        toScheduledRrulePatchKey(occurrence.toISOString())
                    ]
                    const newSession: ScheduledSessionOfUser = {
                        ...session,
                        scheduledStartTime: moment(occurrence).toISOString(),
                    }

                    if (patch && patch.scheduledEndTime) {
                        newSession.scheduledEndTime = moment(patch.scheduledEndTime).toISOString()
                    } else {
                        newSession.scheduledEndTime = moment(session.scheduledEndTime)
                            .set({
                                year: moment(occurrence).year(),
                                month: moment(occurrence).month(),
                                date: moment(occurrence).date(),
                            })
                            .toISOString()
                    }

                    result.push(newSession)
                }
            } catch (error) {
                console.error('Error processing RRULE:', error)
                // Optionally handle the error, e.g., skip this session
            }
        } else {
            const startTimeDate = new Date(session.scheduledStartTime)
            if (startTimeDate >= windowStart && startTimeDate <= windowEnd) {
                result.push({ ...session })
            }
        }
    }

    result.sort((a, b) => new Date(a.scheduledStartTime).getTime() - new Date(b.scheduledStartTime).getTime())

    return result
}

export function toScheduledRrulePatchKey(isoStr: string) {
    // normalize time zone
    const str = moment(isoStr).toISOString()
    // remove ".000" from "2025-01-01T13:00:00.000Z" since "." in key
    // does not play well with mongodb.
    return str.replace(/\.[0-9]+Z$/, 'Z')
}
